import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Users, Building, Phone, Calendar, Activity, Trash2, Moon, Mail
} from 'lucide-react';

/**
 * "Utilisateurs" tab — full account table with test/dormant badges,
 * profile-completion reminder action, and bulk purge of test accounts.
 */
export default function UsersTab({
  users,
  purgingTests,
  purgeTestAccounts,
  sendProfileCompletionReminders,
  formatDate,
  formatRelativeActivity,
}) {
  const testCount = users.filter(u => u.is_test_account).length;
  const dormantCount = users.filter(u => u.is_dormant && !u.is_test_account).length;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Utilisateurs inscrits ({users.length})
            {testCount > 0 && (
              <span className="text-xs font-normal text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                {testCount} compte(s) test
              </span>
            )}
            {dormantCount > 0 && (
              <span className="text-xs font-normal text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                {dormantCount} dormant(s)
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={sendProfileCompletionReminders}
              className="text-violet-700 border-violet-200 hover:bg-violet-50 hover:text-violet-800"
              data-testid="send-profile-completion-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Relancer profils incomplets
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={purgeTestAccounts}
              disabled={purgingTests || testCount === 0}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="purge-test-accounts-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {purgingTests ? 'Suppression…' : 'Purger comptes test'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <p className="text-center text-slate-500 py-8">Aucun utilisateur</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 font-medium text-slate-600">Utilisateur</th>
                  <th className="pb-3 font-medium text-slate-600">Entreprise</th>
                  <th className="pb-3 font-medium text-slate-600">Contact</th>
                  <th className="pb-3 font-medium text-slate-600">Abonnement</th>
                  <th className="pb-3 font-medium text-slate-600">Inscription</th>
                  <th className="pb-3 font-medium text-slate-600">Dernière activité</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const activity = formatRelativeActivity(u);
                  return (
                    <tr key={u.id} className={`border-b hover:bg-slate-50 ${u.is_test_account ? 'bg-amber-50/40' : ''}`} data-testid={`user-row-${u.email}`}>
                      <td className="py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                            <span className="text-violet-700 font-bold">
                              {(u.first_name?.[0] || u.full_name?.[0] || u.email[0]).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 flex items-center gap-1.5">
                              {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.full_name || 'N/A'}
                              {u.is_test_account && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                  test
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{u.company_name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="py-4">
                        {u.phone_number && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700">{u.phone_number}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          u.subscription_tier === 'free'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-violet-100 text-violet-700'
                        }`}>
                          {u.subscription_tier || 'free'}
                        </span>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(u.created_at)}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className={`flex items-center gap-2 text-sm ${activity.dormant ? 'text-amber-700' : 'text-emerald-700'}`}>
                          {activity.dormant ? <Moon className="w-4 h-4" /> : <Activity className="w-4 h-4" />}
                          <span>{activity.label}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
